import { useLocation, matchPath, Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbList,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbSeparator,
  BreadcrumbPage,
} from "@/components/ui/breadcrumb";
import { Titles } from "@/interfaces/enums";
import React from "react";
import { useTranslation } from "react-i18next";

const DynamicBreadcrumb = () => {
  const location = useLocation();
  const { t } = useTranslation();
  const pathnames = location.pathname.split("/").filter((x) => x);

  // Helper to find the title for a given path
  const getBreadcrumbTitle = (currentPath: string) => {
    // Check for exact match first
    if (Titles[currentPath as keyof typeof Titles]) {
      return Titles[currentPath as keyof typeof Titles];
    }

    // Check for dynamic routes
    const foundContext = Object.keys(Titles).find((key) => {
      return matchPath(key, currentPath);
    });

    if (foundContext) {
      const match = matchPath(foundContext, currentPath);
      const title = Titles[foundContext as keyof typeof Titles];

      if (title === "Order" && match?.params?.id) {
        return match.params.id;
      }

      return title;
    }

    return null;
  };

  const breadcrumbs = pathnames
    .map((_, index) => {
      const routeTo = `/${pathnames.slice(0, index + 1).join("/")}`;
      const title = getBreadcrumbTitle(routeTo);
      // If we have a title, we show it.
      // If not, we might want to skip it or show the segment name capitalized if desired,
      // but based on requirements "dynamic based on the route", we should prioritize defined titles.
      // For now, if no title resolves, we'll try to prettify the segment or skip if it's just an ID that didn't resolve a specific title.
      // However, the Titles map handles :id routes (e.g., /ccy-conversion/order-router/:id -> "Order")
      // So if matchPath works, we should get "Order" for ".../123"
      return {
        path: routeTo,
        title: title || null,
        isLast: index === pathnames.length - 1,
      };
    })
    .filter((b) => b.title); // Only show breadcrumbs that have a mapped title to avoid showing random IDs as "123" unless intended.

  // Always prepend Dashboard if not at root
  // Actually, checking Titles['/'] is "Dashboard".
  // Let's explicitly handle the root case or ensure it's part of the flow.

  const items = [
    { path: "/", title: "Crosspoint", isLast: location.pathname === "/" },
    ...breadcrumbs,
  ];

  // Deduplicate if root is somehow added twice or if logic overlaps
  const uniqueItems = items.filter(
    (item, index, self) =>
      index === self.findIndex((t) => t.path === item.path),
  );

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {uniqueItems.map((item, index) => {
          if (!item.title) return null;

          return (
            <React.Fragment key={item.path}>
              <BreadcrumbItem className={index === 0 ? "hidden md:block" : ""}>
                {item.isLast || item.title === "Admin" ? (
                  <BreadcrumbPage
                    className={!item.isLast ? "text-muted-foreground" : ""}
                  >
                    {t(item.title)}
                  </BreadcrumbPage>
                ) : (
                  <BreadcrumbLink asChild>
                    <Link to={item.path}>{t(item.title)}</Link>
                  </BreadcrumbLink>
                )}
              </BreadcrumbItem>
              {!item.isLast && (
                <BreadcrumbSeparator
                  className={index === 0 ? "hidden md:block" : ""}
                />
              )}
            </React.Fragment>
          );
        })}
      </BreadcrumbList>
    </Breadcrumb>
  );
};

export default DynamicBreadcrumb;
